import CSREntry from 'src/clientRoutes/_app.tags.model.$tag.csr'
import FullSSREntry from 'src/clientRoutes/_app.tags.model.$tag.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { type ListGenerationModelsQueryVariables } from 'src/services/apollo/generated/graphql'
import {
  getPosSeqAndFeedForModelListQuery,
  OrderOption,
  translateOrderOptionToQueryOrderBy,
} from 'src/services/helpers/orderOptionParams'
import { createTimeRange } from 'src/utils/dayjs'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import {
  buildTagItemListJsonLd,
  PROD_DOMAIN,
  tagMeta,
  tagMetaLoader,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.tags.model.$tag'

function buildModelTagApolloVariables(
  requestUrl: string,
  tagSlug?: string,
  tackId?: string,
) {
  const url = new URL(requestUrl)
  const keyword = url.searchParams.get('q') ?? undefined
  const order = url.searchParams.get('order') ?? OrderOption.trending
  const type = url.searchParams.get('type') ?? undefined
  const timeRange = url.searchParams.get('timeRange') ?? undefined
  const { posSeq, feed } = getPosSeqAndFeedForModelListQuery({
    order,
    keyword: keyword ?? undefined,
  })

  return {
    order,
    variables: {
      tag: tackId ? undefined : tagSlug,
      tackId,
      keyword,
      types: type ? (type.split(',') as never[]) : undefined,
      feed,
      orderBy: translateOrderOptionToQueryOrderBy(order),
      timeRange: createTimeRange(timeRange ?? ''),
      ...(posSeq ? { first: 24 } : { last: 24 }),
    } satisfies ListGenerationModelsQueryVariables,
  }
}

export const handle = {
  name: 'tags/model',
  track: { scene: 'tags-model' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

export async function loader({ params, request, context }: Route.LoaderArgs) {
  const {
    fetchListGenerationModelsForSSR,
    fetchListTagModelsForSSR,
    fetchTagForSSR,
    fetchTackByCodeNameForSSR,
  } = await import('src/utils/ssr-meta/graphql/tag.server')
  const { t } = getI18n(context)

  const [tag, tack] = await Promise.all([
    params.tag ? fetchTagForSSR(params.tag) : Promise.resolve(null),
    params.tag ? fetchTackByCodeNameForSSR(params.tag) : Promise.resolve(null),
  ])
  const displayName = tag?.displayName || tag?.name || params.tag
  const modelTagApollo = buildModelTagApolloVariables(
    request.url,
    params.tag ?? undefined,
    tack?.id ?? undefined,
  )
  const listVariables = tack?.id
    ? modelTagApollo.variables
    : {
        ...modelTagApollo.variables,
        tag: params.tag ?? undefined,
        tackId: undefined,
      }
  const listData = params.tag
    ? await fetchListGenerationModelsForSSR(listVariables)
    : null
  const previewConnection = params.tag
    ? await fetchListTagModelsForSSR({
        ...listVariables,
      })
    : null

  // Build a schema.org `ItemList` JSON-LD describing the first-screen
  // models for this tag, so crawlers can surface the page as a curated
  // collection in search results. Prefer the cheap preview connection;
  // fall back to the full list data if preview was empty.
  const preliminaryMeta = tagMetaLoader({
    type: 'model',
    tagSlug: params.tag,
    tag,
    requestUrl: request.url,
  })
  const modelItemListSourceEdges =
    previewConnection?.edges ?? listData?.edges ?? []
  const modelItemListJsonLd = displayName
    ? buildTagItemListJsonLd({
        type: 'model',
        displayName,
        tagDescription: preliminaryMeta.description,
        canonicalUrl: preliminaryMeta.canonicalUrl,
        entries: modelItemListSourceEdges
          .map(edge => edge?.node)
          .filter(
            (node): node is NonNullable<typeof node> =>
              !!node && typeof node.id === 'string',
          )
          .map(node => {
            const title = node.title?.trim() || undefined
            const media = node.media
            const thumb =
              media?.thumbnailUrl ??
              media?.urls?.find(u => u?.variant === 'STILL_THUMBNAIL')?.url ??
              media?.urls?.find(u => u?.variant === 'PUBLIC')?.url ??
              undefined
            return {
              id: node.id,
              url: `https://${PROD_DOMAIN}/model/${node.id}`,
              name: title,
              image: thumb ?? undefined,
            }
          }),
      })
    : undefined

  const meta = tagMetaLoader({
    type: 'model',
    tagSlug: params.tag,
    tag,
    requestUrl: request.url,
    jsonLd: modelItemListJsonLd,
  })

  // `serverGraphqlFetch` strips Apollo's automatic `__typename` injection,
  // so the server-fetched tack / connections come back without
  // `__typename`. When that data is later written into the route-scoped
  // Apollo cache (via `createRouteApolloClient`'s `writeQuery`),
  // InMemoryCache can't normalize entities and later reads come back
  // with all scalar fields as `null`. Patch `__typename` back in here
  // so that client-side `useGetTackByCodeName` / list hooks hit the
  // seeded cache with valid data.
  const tackWithTypename = tack
    ? { ...tack, __typename: 'Tack' as const }
    : null
  const addTypenameToGenModelConnection = <
    T extends {
      edges?: ReadonlyArray<unknown> | null
      pageInfo?: unknown
    } | null,
  >(
    conn: T,
  ) => {
    if (!conn) return conn
    return {
      __typename: 'GenerationModelConnection' as const,
      ...conn,
      edges: (conn.edges ?? []).map((edge: unknown): unknown => {
        if (!edge || typeof edge !== 'object') return edge
        const e = edge as { node?: unknown; cursor?: string }
        return {
          __typename: 'GenerationModelEdge' as const,
          ...e,
          node:
            e.node && typeof e.node === 'object'
              ? {
                  __typename: 'GenerationModel' as const,
                  ...(e.node as object),
                }
              : e.node,
        }
      }),
      pageInfo:
        conn.pageInfo && typeof conn.pageInfo === 'object'
          ? {
              __typename: 'PageInfo' as const,
              ...(conn.pageInfo as object),
            }
          : conn.pageInfo,
    }
  }
  const previewConnectionWithTypename =
    addTypenameToGenModelConnection(previewConnection)
  const listDataWithTypename = addTypenameToGenModelConnection(listData)

  return {
    ...meta,
    tag,
    previewConnectionEdgeCount: previewConnection?.edges?.length ?? -1,
    modelTagApollo: {
      tagSlug: params.tag ?? '',
      tackData: tackWithTypename ? { tack: tackWithTypename } : undefined,
      listVariables,
      listData: listDataWithTypename
        ? { generationModels: listDataWithTypename }
        : undefined,
    },
    initialModelConnectionData: previewConnectionWithTypename ?? undefined,
    tagShell:
      tag && displayName
        ? {
            type: 'model' as const,
            displayName,
            title: meta.title ?? '',
            description: meta.description ?? '',
            image: meta.image,
            canonicalUrl: meta.canonicalUrl,
            heroTitle: t('common:tags.model.hero.title', {
              tag: displayName,
            }),
            heroDescription: t('common:tags.model.hero.description', {
              tag: displayName,
            }),
          }
        : undefined,
  }
}

export function meta({ loaderData }: Route.MetaArgs) {
  return tagMeta(loaderData)
}

/**
 * Dual-mode entry. `'off'` (CSR) path renders `TagPage` directly
 * under `<RootRuntime>`'s global Apollo / QueryClient with no SSR
 * seed; `'on'` (Full SSR) path mounts the route-scoped Apollo
 * bridge defined in `clientRoutes/_app.tags.model.$tag.full-ssr.tsx`.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
